import { useState } from 'react';
import { signOut } from 'aws-amplify/auth';
import { Loader2, Mail, UserPlus, X } from 'lucide-react';
import clsx from 'clsx';
import { useMe } from '../api/me';
import {
  NOMBRE_ROL,
  mensajeDe,
  useCambiarPersona,
  useEquipo,
  useInvitar,
  useReenviar,
  type EstadoPersona,
  type Persona,
  type Rol,
} from '../api/equipo';
import { AyudaGuia, Pregunta } from '../components/Ayuda';
import { config } from '../lib/config';

const WEB = config.webUrl ?? 'https://habitania.info';

export default function AjustesView() {
  const { data: me } = useMe();
  const esAdmin = Boolean(me?.groups.includes('admin'));
  const rol = esAdmin ? 'Administrador' : me?.groups.includes('agent') ? 'Agente' : '—';

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-deep">Ajustes</h1>
        <AyudaGuia seccion="ajustes" />
      </div>

      <div className="mt-6 max-w-lg rounded-2xl border border-deep/10 bg-surface p-6">
        <p className="text-xs font-bold uppercase tracking-widest text-brand">Tu cuenta</p>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-ink/50">Email</dt>
            <dd className="font-semibold text-deep">{me?.email}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-ink/50">Tu rol</dt>
            <dd className="font-semibold text-deep">{rol}</dd>
          </div>
        </dl>
        <button
          onClick={() => signOut()}
          className="mt-6 rounded-full bg-deep px-6 py-2.5 text-sm font-bold text-white hover:bg-brand transition"
        >
          Cerrar sesión
        </button>
      </div>

      {/* Las visitas del equipo a la web cuentan como las de cualquiera y
          engordarían «Tu web». La marca se guarda en el navegador desde el que
          se pulsa (ver web/src/components/Medicion.astro). */}
      <div className="mt-6 max-w-lg rounded-2xl border border-deep/10 bg-surface p-6">
        <p className="text-xs font-bold uppercase tracking-widest text-brand">Tus visitas a la web</p>
        <p className="mt-2 text-sm text-ink/60">
          Cuando entras en habitania.info, tu visita cuenta en «Tu web» como la de cualquiera. Para que
          las tuyas no cuenten, pulsa el botón una vez en cada navegador que uses: el del ordenador y el
          del móvil.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <a
            href={`${WEB}/?no-contar=1`}
            target="_blank"
            rel="noopener"
            className="rounded-full border-2 border-deep px-5 py-2 text-sm font-bold text-deep transition hover:bg-deep hover:text-white"
          >
            No contar mis visitas
          </a>
          <a href={`${WEB}/?contar=1`} target="_blank" rel="noopener" className="text-xs text-ink/50 underline hover:text-brand">
            Volver a contarlas
          </a>
        </div>
      </div>

      {esAdmin && <TuEquipo />}
    </div>
  );
}

// ── Tu equipo (solo admin) ────────────────────────────────────────

const ESTADO: Record<EstadoPersona, { texto: string; clase: string }> = {
  activo: { texto: 'Activo', clase: 'bg-leaf/50 text-deep' },
  pendiente: { texto: 'Invitación pendiente', clase: 'bg-amber-100 text-amber-800' },
  caducada: { texto: 'Invitación caducada', clase: 'bg-red-100 text-red-700' },
  sin_acceso: { texto: 'Sin acceso', clase: 'bg-deep/10 text-ink/55' },
};

function TuEquipo() {
  const { data, isLoading, isError, refetch } = useEquipo(true);
  const [invitando, setInvitando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  return (
    <section className="mt-8 max-w-3xl rounded-2xl border border-deep/10 bg-surface p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-brand">Tu equipo</p>
          <p className="mt-1 text-sm text-ink/60">Quién entra en el CRM y con qué rol.</p>
        </div>
        <button
          onClick={() => { setInvitando(true); setAviso(null); }}
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition hover:bg-deep"
        >
          <UserPlus size={16} /> Invitar a alguien
        </button>
      </div>
      <Pregunta titulo="¿Qué le llega a la persona que invito?">
        Un correo de Habitania con la dirección del CRM, su usuario (su correo) y una contraseña
        provisional. La primera vez que entre, el CRM le pedirá que elija la suya, de 10 caracteres o
        más. La contraseña provisional vale 7 días; si caduca, pulsa «Reenviar invitación». Si esa
        persona ya tenía cuenta en la web, no le llega nada: entra con la contraseña que ya tiene.
      </Pregunta>
      <Pregunta titulo="¿Qué ve un agente y qué ve un administrador?">
        Un administrador lo ve todo. Un agente, todo menos Costes, Servicios, Cuentas, Tu web y este bloque
        de Tu equipo. Puedes cambiar el rol de alguien cuando quieras, pero no el tuyo: así siempre queda al
        menos un administrador.
      </Pregunta>

      {aviso && <p className="mt-4 rounded-xl bg-leaf/30 px-4 py-3 text-sm text-deep">{aviso}</p>}

      {isLoading ? (
        <p className="mt-6 text-sm text-ink/50">Cargando…</p>
      ) : isError || !data ? (
        <div className="mt-6">
          <p className="text-sm font-bold text-red-700">No se ha podido cargar el equipo</p>
          <button onClick={() => refetch()} className="mt-2 rounded-full bg-red-600 px-4 py-1.5 text-sm font-bold text-white">Reintentar</button>
        </div>
      ) : (
        <ul className="mt-5 space-y-2">
          {data.map((p) => <FilaPersona key={p.username} p={p} onAviso={setAviso} />)}
        </ul>
      )}

      {invitando && <InvitarModal onClose={() => setInvitando(false)} onHecho={setAviso} />}
    </section>
  );
}

function FilaPersona({ p, onAviso }: { p: Persona; onAviso: (t: string) => void }) {
  const cambiar = useCambiarPersona();
  const reenviar = useReenviar();
  const error = mensajeDe(cambiar.error) ?? mensajeDe(reenviar.error);
  const ocupado = cambiar.isPending || reenviar.isPending;
  const e = ESTADO[p.estado];

  return (
    <li className={clsx('rounded-2xl border px-4 py-3', p.estado === 'sin_acceso' ? 'border-dashed border-deep/20' : 'border-deep/10')}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-deep">
            {p.nombre || p.email}
            {p.soy_yo && <span className="ml-2 text-xs font-semibold text-ink/45">(tú)</span>}
          </p>
          <p className="truncate text-xs text-ink/55">
            {p.nombre ? p.email : ''}{p.nombre && p.cliente_web ? ' · ' : ''}{p.cliente_web ? 'también es cliente de la web' : ''}
          </p>
        </div>
        <span className={clsx('rounded-full px-2.5 py-0.5 text-xs font-bold', e.clase)}>{e.texto}</span>

        {p.estado !== 'sin_acceso' && p.rol && (
          <select
            value={p.rol}
            disabled={p.soy_yo || ocupado}
            aria-label="Rol"
            title={p.soy_yo ? 'No puedes cambiar tu propio rol' : undefined}
            onChange={(ev) => cambiar.mutate({ username: p.username, rol: ev.target.value as Rol })}
            className="rounded-xl border border-deep/15 bg-surface px-2 py-1 text-xs font-bold text-deep disabled:opacity-60"
          >
            <option value="agent">{NOMBRE_ROL.agent}</option>
            <option value="admin">{NOMBRE_ROL.admin}</option>
          </select>
        )}
        {(p.estado === 'pendiente' || p.estado === 'caducada') && (
          <button
            onClick={() => reenviar.mutate(p.username, { onSuccess: () => onAviso(`Invitación reenviada a ${p.email}.`) })}
            disabled={ocupado}
            className="flex items-center gap-1.5 rounded-full border border-deep/15 px-3 py-1 text-xs font-bold text-ink/65 hover:border-brand hover:text-brand disabled:opacity-50"
          >
            <Mail size={12} /> Reenviar invitación
          </button>
        )}
        {p.estado === 'sin_acceso' ? (
          <button
            onClick={() => cambiar.mutate({ username: p.username, acceso: true }, { onSuccess: () => onAviso(`${p.email} vuelve a tener acceso, como Agente.`) })}
            disabled={ocupado}
            className="rounded-full bg-deep px-3 py-1 text-xs font-bold text-white hover:bg-brand disabled:opacity-50"
          >
            Dar acceso de nuevo
          </button>
        ) : !p.soy_yo && (
          <button
            onClick={() => {
              if (window.confirm(`¿Quitar el acceso al CRM a ${p.email}? Podrás devolvérselo cuando quieras.`))
                cambiar.mutate({ username: p.username, acceso: false }, { onSuccess: () => onAviso(`${p.email} ya no puede entrar en el CRM.`) });
            }}
            disabled={ocupado}
            className="rounded-full px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            Quitar acceso
          </button>
        )}
        {ocupado && <Loader2 size={14} className="animate-spin text-brand" />}
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </li>
  );
}

function InvitarModal({ onClose, onHecho }: { onClose: () => void; onHecho: (t: string) => void }) {
  const invitar = useInvitar();
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<Rol>('agent');
  const error = mensajeDe(invitar.error);

  const enviar = () =>
    invitar.mutate(
      { email, nombre, rol },
      {
        onSuccess: (r) => {
          onHecho(
            r.ya_tenia_cuenta
              ? `${r.persona.email} ya tenía cuenta en la web: ya puede entrar en el CRM con esa misma contraseña. No se le ha mandado ningún correo.`
              : `Invitación enviada a ${r.persona.email}. Le llegará un correo con su contraseña provisional.`,
          );
          onClose();
        },
      },
    );

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-deep/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-deep">Invitar a alguien</h2>
          <button onClick={onClose} aria-label="Cerrar" className="text-ink/40 hover:text-deep"><X size={20} /></button>
        </div>
        <div className="mt-4 space-y-3">
          <Campo label="Correo *">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ana@habitania.info" className={INPUT} autoFocus />
          </Campo>
          <Campo label="Nombre">
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ana Ruiz" maxLength={80} className={INPUT} />
          </Campo>
          <Campo label="Rol">
            <select value={rol} onChange={(e) => setRol(e.target.value as Rol)} className={INPUT}>
              <option value="agent">Agente: todo menos Costes, Servicios, Cuentas, Tu web y Tu equipo</option>
              <option value="admin">Administrador: todo</option>
            </select>
          </Campo>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-bold text-ink/55 hover:text-deep">Cancelar</button>
          <button
            onClick={enviar}
            disabled={invitar.isPending || !email.trim()}
            className="flex items-center gap-2 rounded-full bg-brand px-5 py-2 text-sm font-bold text-white hover:bg-deep disabled:opacity-50"
          >
            {invitar.isPending && <Loader2 size={14} className="animate-spin" />}
            Enviar invitación
          </button>
        </div>
      </div>
    </div>
  );
}

const INPUT =
  'w-full rounded-xl border border-deep/15 bg-surface px-3 py-2 text-sm text-deep outline-none placeholder:text-ink/40 focus:border-brand';

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold text-ink/50">{label}</span>
      {children}
    </label>
  );
}
